<template>
    <div class="OrgPaidProfileLevels mb-0">
        <CCollapsible v-model="expanded" chevron-hidden class="group" no-padding :plain="preview">
            <template v-if="!expanded || selectedLevelLabel" #header>
                <header class="p-4">
                    Zvolený profil organizácie: <CBadge type="info">{{ selectedLevelLabel }}</CBadge>
                </header>
            </template>

            <template #button>
                <div
                    :class="{
                        'text-primary-950 dark:text-primary-300 group-hover:text-primary-800 group-hover:dark:text-primary-300': !expanded,
                        'text-gray-500 dark:text-txt-body': expanded
                    }"
                >
                    <template v-if="!expanded">
                        <CIcon name="edit-16" size="small" />
                        <span class="text-sm font-medium group-hover:underline">Zmeniť</span>
                    </template>

                    <span v-else-if="expanded && selectedLevelLabel" class="text-sm font-medium">Skryť</span>
                </div>
            </template>

            <div class="flex flex-col gap-5 pb-6" :class="{ 'pt-6': !selectedLevelLabel && !preview }">
                <article
                    class="custom-rows grid grid-flow-col px-4"
                    :class="freePlanInfoPlacement === 'beforePlans' ? 'order-2' : 'order-1'"
                    :style="`--c-rows:${rows * cols}; --c-rows-sm:${rows}; --c-cols:${cols}`"
                >
                    <template v-for="(options, level) in sections" :key="`${level}-template`">
                        <div
                            class="relative flex place-content-center rounded-t-lg pb-2 pt-8"
                            :class="{
                                'bg-primary-50 dark:bg-primary-950/40': level === activeLevel,
                                'border-x border-t border-primary-100': isLevelHighlighted(level)
                            }"
                        >
                            <CBadge
                                v-if="isLevelHighlighted(level)"
                                class="absolute right-0 top-0 rounded-br-none rounded-tl-none font-normal"
                                type="info"
                            >
                                obľúbené
                            </CBadge>

                            <header class="text-center">
                                <div
                                    class="flex items-center justify-center gap-3 rounded-2xl px-6 py-4 text-primary-900 dark:text-primary-300"
                                >
                                    <CIcon v-if="false" :name="options.icon" size="normal" />
                                    <CHeading class="mb-0 uppercase" variant="2">
                                        {{ levels[level] }}
                                    </CHeading>
                                </div>

                                <CHeading class="font-normal" variant="2">
                                    {{ options.price }} € <span class="text-lg">/ {{ options.duration }}</span>
                                </CHeading>
                            </header>
                        </div>

                        <CList
                            v-for="(item, idx) in options.list"
                            :key="`${level}-${idx}`"
                            class="w-full py-2 text-left"
                            :class="{
                                'bg-primary-50 dark:bg-primary-950/40': level === activeLevel,
                                'hidden md:block': !item,
                                'border-x border-primary-100': isLevelHighlighted(level)
                            }"
                            variant="muted"
                        >
                            <li
                                v-if="item === false"
                                class="ml-0 flex h-full items-center justify-center px-6 before:hidden"
                            ></li>
                            <li v-else-if="item.type === 'text'" class="px-6 text-black dark:text-txt-headings">{{ item.value }}</li>
                            <li v-else class="px-6 text-black dark:text-txt-headings">
                                <template v-if="item.level === OrgGovPaidProfileLevel.PARTNER">
                                    <span class="font-semibold text-[#1dc8cc]"> EXTRA rozšírený </span>
                                </template>
                                <template v-if="item.level === OrgGovPaidProfileLevel.PREMIUM">
                                    <span class="font-semibold text-[#1dc8cc]"> Rozšírený </span>
                                </template>
                                <template v-if="item.level === OrgGovPaidProfileLevel.PLUS">
                                    <span class="font-semibold text-[#1dc8cc]"> Základný </span>
                                </template>
                                <b>profil</b> vašej organizácie na GROWNi
                                <a href="#" class="link text-primary-800 dark:text-primary-300" @click.prevent="openPreview(item.level)">
                                    {{ item.value }}
                                </a>
                            </li>
                        </CList>

                        <div
                            v-if="!preview"
                            :key="`${level}-footer`"
                            class="flex place-content-center rounded-b-lg px-6 py-8"
                            :class="{
                                'bg-primary-50 dark:bg-primary-950/40': level === activeLevel,
                                'border-x border-b border-primary-100': isLevelHighlighted(level)
                            }"
                        >
                            <footer class="flex flex-col gap-2">
                                <CButton
                                    size="large"
                                    :variant="
                                        level === activeLevel || !selectedLevelLabel ? 'primary' : 'primary-outline'
                                    "
                                    @click="update(level)"
                                >
                                    Vybrať
                                </CButton>

                                <small class="max-w-[20ch] text-center text-gray-500 dark:text-txt-body">
                                    Budeme vás kontaktovať ohľadom fakturácie.
                                </small>
                            </footer>
                        </div>
                    </template>
                </article>

                <footer
                    class="bg-gray-100 dark:bg-subtle px-6 py-8 text-center"
                    :class="[
                        freePlanInfoPlacement === 'beforePlans' ? 'order-1' : 'order-2',
                        { 'rounded-lg': preview }
                    ]"
                >
                    <p class="mb-0 block">
                        Ak nepotrebuje vaša organizácia zamestnancov, máte možnosť využiť aj bezplatnú
                        <strong>verziu FREE</strong>, v ktorej budete mať iba <strong>oklieštený profil</strong> na
                        GROWNi
                        <a class="link text-primary-800 dark:text-primary-300" href="#" @click.prevent="openPreview(null)">
                            (zobraziť ukážku)
                        </a>

                        a nebudete môcť zverejňovať pracovné ponuky a stáže. Odporúčame pre sekcie dobrovoľníctvo a
                        vzdelávanie
                        <CButton
                            v-if="!preview"
                            class="inline-flex min-h-0 pt-0"
                            variant="primary-link"
                            @click="update(null)"
                        >
                            Vybrať FREE
                        </CButton>
                    </p>
                </footer>

                <aside class="px-6 text-center" :class="{ 'py-8': !preview }">
                    <p class="mx-auto mb-0 block max-w-prose">
                        Sme nezisková organizácia a financie využívame na zabezpečenie fungovania platformy a jej
                        neustále zlepšovanie. Každej spolupráci sa venujeme personalizovane.<br />Kontaktujte nás,
                        prosím, na <a class="link" href="mailto:pracuj@growni.sk">pracuj@growni.sk</a>.
                    </p>
                </aside>
            </div>
        </CCollapsible>
    </div>
</template>

<script setup lang="ts">
import { orgGovPaidProfileLevelDictionary } from '#shared/types/dictionary'
import type { Org } from '#shared/types/Org'
import { OrgGovPaidProfileLevel, OrgType } from '#shared/types/Org'
import OrgGovProfilePreviewModal from '@/components/orgs/modals/OrgGovProfilePreviewModal.vue'
import { useOrgs } from '@/composables/orgs/useOrgs'
import { orgProfileLevelOptions } from '@/constants/orgProfileLevels'
import type { PropType } from 'vue'
import { computed, ref, watch } from 'vue'
import { useModal } from 'vue-final-modal'

// --- Props a emits ---
const props = defineProps({
    value: {
        type: [Object, null, undefined] as PropType<Org['paidProfileInfo']>,
        default: undefined
    },
    preview: {
        type: Boolean,
        default: false
    },
    freePlanInfoPlacement: {
        type: String as PropType<'afterPlans' | 'beforePlans'>,
        default: 'afterPlans'
    }
})

const emit = defineEmits(['input', 'onExpand'])

const selectedLevel = ref<OrgGovPaidProfileLevel | null>(null)

const {
    open: openProfilePreviewModal,
    close: closeProfilePreviewModal,
    patchOptions
} = useModal({
    component: OrgGovProfilePreviewModal
})

function openPreview(level: OrgGovPaidProfileLevel | null) {
    selectedLevel.value = level
    patchOptions({
        attrs: {
            level,
            preview: props.preview,
            onClose: () => closeProfilePreviewModal()
        }
    })
    openProfilePreviewModal()
}

// --- Orgs composable ---
const { getLevelLabel } = useOrgs()

// --- Stavy ---
const expanded = ref(props.value === undefined)
const levels = orgGovPaidProfileLevelDictionary
const sections = orgProfileLevelOptions
const highlightedLevel = ref(OrgGovPaidProfileLevel.PREMIUM)

const cols = computed(() => {
    return Object.keys(sections).length
})

const rows = computed(() => {
    const firstSection = Object.values(sections)[0]
    if (!firstSection) return 0

    return firstSection.list.length + (props.preview ? 1 : 2)
})

// --- Aktívny / zvolený level ---
const activeLevel = computed(() => props.value?.plan?.level)
const selectedLevelLabel = computed(() => {
    if (props.value === undefined) return undefined
    return getLevelLabel({ paidProfileInfo: props.value, type: OrgType.publicSector })
})

// --- Watcher na expand ---
watch(expanded, (isExpanded) => {
    emit('onExpand', isExpanded)
})

// --- Funkcia na výber levelu ---
function update(level: OrgGovPaidProfileLevel | null) {
    expanded.value = false
    emit('input', level)
}

// --- Zvýraznenie levelu ---
function isLevelHighlighted(level: OrgGovPaidProfileLevel) {
    return highlightedLevel.value === level && !activeLevel.value && !props.preview
}
</script>

<style scoped>
.custom-rows {
    grid-template-rows: repeat(var(--c-rows), auto);
    grid-template-columns: 1fr;

    @media (min-width: 768px) {
        grid-template-rows: repeat(var(--c-rows-sm), auto);
        grid-template-columns: repeat(var(--c-cols), 1fr);
    }
}
</style>
